import React, { useEffect, useState } from "react";
import { api, API as API_BASE } from "../../lib/api";
import { toast } from "sonner";
import {
  Lock, CheckCircle2, Circle, ShieldCheck, Banknote, Receipt, Heart,
  Download, Save, Loader2,
} from "lucide-react";

/**
 * Post-Approval Manager — unlocks the moment Charity Status flips to
 * "registered". While the application is in preparation, this panel shows a
 * preview of what will be available so trustees can plan.
 */
export default function PostApprovalManager() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/post-approval/summary");
      setS(r.data);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  if (!s) return <div className="h-40 bg-pearl/50 animate-pulse rounded-sm" />;

  const locked = !s.is_registered;

  return (
    <div className="space-y-6" data-testid="post-approval-manager">
      <StatusBanner s={s} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <GiftAidCard s={s} locked={locked} onReload={load} busy={busy} setBusy={setBusy} />
        <BankCard s={s} locked={locked} onReload={load} busy={busy} setBusy={setBusy} />
        <BookkeepingCard s={s} locked={locked} onReload={load} busy={busy} setBusy={setBusy} />
      </div>
      {locked && <PreviewFooter />}
    </div>
  );
}

function StatusBanner({ s }) {
  if (s.is_registered) {
    return (
      <div className="bg-green-50 border border-green-300 rounded-sm p-5 flex items-start gap-3" data-testid="post-approval-banner-registered">
        <CheckCircle2 size={22} className="text-green-700 mt-0.5 shrink-0" />
        <div>
          <h3 className="font-display text-lg text-navy">Registered charity · No. {s.charity_number}</h3>
          <p className="text-sm text-slate-600 mt-1">
            Approved {s.registered_at?.slice(0, 10) || "—"}. The post-approval modules below are now unlocked.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-sm p-5 flex items-start gap-3" data-testid="post-approval-banner-pending">
      <Lock size={22} className="text-amber-700 mt-0.5 shrink-0" />
      <div>
        <h3 className="font-display text-lg text-navy">Application in preparation</h3>
        <p className="text-sm text-slate-600 mt-1">
          These modules unlock the moment the Charity Commission grants registration. Update the
          charity status in <strong>Settings → Charity Status</strong> with the registered charity
          number and registration date — everything below will activate instantly.
        </p>
      </div>
    </div>
  );
}

function GiftAidCard({ s, locked, onReload, busy, setBusy }) {
  const [enabling, setEnabling] = useState(false);

  const toggle = async () => {
    setEnabling(true);
    try {
      await api.put("/admin/charity/status", { gift_aid_enabled: !s.gift_aid_enabled });
      toast.success(s.gift_aid_enabled ? "Gift Aid disabled" : "Gift Aid enabled");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setEnabling(false);
  };

  return (
    <div className={`bg-white border rounded-sm p-5 ${locked ? "opacity-70 border-navy/10" : "border-navy/15"}`} data-testid="gift-aid-card">
      <div className="flex items-center gap-2 mb-2">
        <Heart size={18} className="text-saffron" />
        <h3 className="font-display text-lg text-navy">Gift Aid</h3>
        {locked && <Lock size={13} className="text-amber-600 ml-auto" />}
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">
        Reclaim 25p in tax for every £1 donated by a UK taxpayer. Eligible donors carry a
        Gift Aid declaration; the system collects HMRC-required name + address fields and
        exports them as an R68(i)-style CSV for your annual claim.
      </p>
      <dl className="mt-4 text-sm space-y-1">
        <Row k="Status" v={s.gift_aid_enabled ? "Enabled" : "Disabled"} />
        <Row k="Eligible donations" v={s.gift_aid_eligible_count} />
        <Row k="Eligible total" v={`£${s.gift_aid_eligible_gbp.toFixed(2)}`} />
        <Row k="Estimated reclaim" v={`£${s.estimated_gift_aid_reclaim_gbp.toFixed(2)}`} bold />
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={toggle}
          disabled={enabling}
          data-testid="gift-aid-toggle"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-navy text-pearl rounded-sm text-xs hover:bg-navy-light disabled:opacity-50"
        >
          {enabling ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {s.gift_aid_enabled ? "Disable Gift Aid" : "Enable Gift Aid"}
        </button>
        <a
          href={`${API_BASE}/admin/post-approval/gift-aid-claim.csv`}
          target="_blank" rel="noreferrer"
          data-testid="gift-aid-export"
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium ${
            locked ? "bg-slate-200 text-slate-400 pointer-events-none" : "bg-saffron text-navy hover:bg-saffron-light"
          }`}
        >
          <Download size={12} /> HMRC CSV
        </a>
      </div>
    </div>
  );
}

function BankCard({ s, locked, onReload, busy, setBusy }) {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({
    bank_name: "", account_name: "", sort_code: "", account_number_last4: "", opened_on: "", notes: "",
  });

  useEffect(() => {
    api.get("/admin/post-approval/bank").then((r) => {
      if (r.data && Object.keys(r.data).length) {
        setDoc(r.data);
        setForm((f) => ({ ...f, ...r.data }));
      }
    }).catch(() => {});
  }, []);

  const save = async () => {
    if (locked) return toast.error("Unlock charity status first.");
    setBusy(true);
    try {
      const r = await api.put("/admin/post-approval/bank", form);
      setDoc(r.data);
      toast.success("Bank record saved");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div className={`bg-white border rounded-sm p-5 ${locked ? "opacity-70 border-navy/10" : "border-navy/15"}`} data-testid="bank-card">
      <div className="flex items-center gap-2 mb-2">
        <Banknote size={18} className="text-saffron" />
        <h3 className="font-display text-lg text-navy">Charity bank account</h3>
        {locked && <Lock size={13} className="text-amber-600 ml-auto" />}
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">
        Record the metadata of the charity's bank account (e.g., CAF Bank, Reliance, Metro). We
        store only the last 4 digits of the account number — never the full number.
      </p>
      <div className="mt-4 space-y-2 text-sm">
        <Input label="Bank name" value={form.bank_name} disabled={locked} onChange={(v) => setForm({ ...form, bank_name: v })} testid="bank-name" />
        <Input label="Account name" value={form.account_name} disabled={locked} onChange={(v) => setForm({ ...form, account_name: v })} testid="bank-account-name" />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Sort code" placeholder="12-34-56" value={form.sort_code} disabled={locked} onChange={(v) => setForm({ ...form, sort_code: v })} testid="bank-sort-code" />
          <Input label="Acct. last 4" placeholder="1234" value={form.account_number_last4} disabled={locked} onChange={(v) => setForm({ ...form, account_number_last4: v.slice(0, 4) })} testid="bank-last4" />
        </div>
        <Input label="Opened on" type="date" value={form.opened_on} disabled={locked} onChange={(v) => setForm({ ...form, opened_on: v })} testid="bank-opened" />
      </div>
      <button
        onClick={save}
        disabled={locked || busy}
        data-testid="bank-save"
        className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 bg-navy text-pearl rounded-sm text-xs hover:bg-navy-light disabled:opacity-50"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save bank record
      </button>
      {doc?.updated_at && (
        <p className="mt-2 text-[10px] text-slate-400">Last updated {new Date(doc.updated_at).toLocaleString()} by {doc.updated_by}</p>
      )}
    </div>
  );
}

function BookkeepingCard({ s, locked, onReload, busy, setBusy }) {
  const [year, setYear] = useState(new Date().getFullYear());

  const markStarted = async () => {
    setBusy(true);
    try {
      await api.put("/admin/charity/status", { bookkeeping_started: !s.bookkeeping_started });
      toast.success("Bookkeeping flag updated");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div className={`bg-white border rounded-sm p-5 ${locked ? "opacity-70 border-navy/10" : "border-navy/15"}`} data-testid="bookkeeping-card">
      <div className="flex items-center gap-2 mb-2">
        <Receipt size={18} className="text-saffron" />
        <h3 className="font-display text-lg text-navy">Bookkeeping</h3>
        {locked && <Lock size={13} className="text-amber-600 ml-auto" />}
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">
        Export every paid transaction as a Xero / QuickBooks-ready CSV — one row per
        donation or membership, categorised by account, ready to hand to your bookkeeper.
      </p>
      <div className="mt-4 flex items-center gap-2 text-sm">
        <label className="text-xs text-slate-600">Year</label>
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="border border-navy/15 rounded-sm px-2 py-1 text-xs" data-testid="bookkeeping-year">
          {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <dl className="mt-4 text-sm space-y-1">
        <Row k="Started" v={s.bookkeeping_started ? "Yes" : "Not yet"} />
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={markStarted}
          disabled={busy}
          data-testid="bookkeeping-toggle"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-navy text-pearl rounded-sm text-xs hover:bg-navy-light disabled:opacity-50"
        >
          {s.bookkeeping_started ? <Circle size={12} /> : <CheckCircle2 size={12} />}
          {s.bookkeeping_started ? "Mark not started" : "Mark started"}
        </button>
        <a
          href={`${API_BASE}/admin/post-approval/bookkeeping.csv?year=${year}`}
          target="_blank" rel="noreferrer"
          data-testid="bookkeeping-export"
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium ${
            locked ? "bg-slate-200 text-slate-400 pointer-events-none" : "bg-saffron text-navy hover:bg-saffron-light"
          }`}
        >
          <Download size={12} /> Export CSV
        </a>
      </div>
    </div>
  );
}

function PreviewFooter() {
  return (
    <div className="text-xs text-slate-500 italic bg-pearl/40 border border-navy/5 rounded-sm p-4">
      <ShieldCheck size={13} className="inline mr-1.5 text-saffron" />
      These modules will activate the instant the Charity Commission's decision is recorded above.
      They will not collect or expose any restricted data while the application is in preparation.
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-slate-500 text-xs">{k}</dt>
      <dd className={`text-navy text-xs ${bold ? "font-semibold" : ""}`}>{v}</dd>
    </div>
  );
}

function Input({ label, value, onChange, disabled, placeholder, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</span>
      <input
        type={type}
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-navy/15 rounded-sm px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
      />
    </label>
  );
}
